import React, { useState, useMemo, useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { deleteParamConfig } from "../../../redux/actions/deleteParameterConfigSlice";
import { deleteParameterConfiguration } from "../../../common/endpoints";
import { getActiveParameterConfigurations } from "../../../redux/actions/activeParameterConfigSlice";
import { toast } from 'react-toastify';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { saveParameterOverride } from "../../../redux/actions/parameterOverrideSlice";
import ButtonComponent from "../../../components/common/button/Button";
import ParamSlidersModal from "./ParamSlidersModal";
import DataTable from "../../../components/common/datatable/DataTable";

const OperationalOverrides = ({ rules, setRules, canManageOverrides = true }) => {
  const [open, setOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedRule, setSelectedRule] = useState(null);
  const [disabledSaveIds, setDisabledSaveIds] = useState([]);
  const [highlightedId, setHighlightedId] = useState(null);
  const gridApiRef = React.useRef(null);
  const getRowKey = (row) => {
    if (!row) return "";
    const p = Array.isArray(row.plant) ? row.plant.join(',') : String(row.plant ?? "");
    const m = Array.isArray(row.material) ? row.material.join(',') : String(row.material ?? "");
    const sValue = row.scode ?? row.sCode;
    const s = Array.isArray(sValue) ? sValue.join(',') : String(sValue ?? "");
    const r = Array.isArray(row.mrpController) ? row.mrpController.join(',') : String(row.mrpController ?? "");
    return `${p}|${m}|${s}|${r}`;
  };
  const dispatch = useDispatch();
  const deleteLoading = useSelector((state) => state.deleteParameterConfig?.loading);
  const EMPTY_ARR = React.useMemo(() => [], []);
  const activeConfigs = useSelector((state) => state.activeParameterConfig?.data?.data ?? EMPTY_ARR);
  const activeConfigsLoading = useSelector((state) => state.activeParameterConfig?.loading ?? false);
  const activeConfigsError = useSelector((state) => state.activeParameterConfig?.error ?? null);

  // Fetch active parameter configurations on mount
  useEffect(() => {
    dispatch(getActiveParameterConfigurations());
  }, [dispatch]);

  // Keep local rules in sync with backend
  useEffect(() => {
    if (Array.isArray(activeConfigs)) {
      setRules(activeConfigs);
    }
  }, [activeConfigs, setRules]);

  // Initialize disabled save buttons from backend `saveDisabled` flag
  useEffect(() => {
    if (Array.isArray(activeConfigs) && activeConfigs.length > 0) {
      try {
        const keys = activeConfigs
          .filter((r) => r?.saveDisabled)
          .map((r) => getRowKey(r))
          .filter(Boolean);
        setDisabledSaveIds(keys);
      } catch (e) {
        // ignore
      }
    }
  }, [activeConfigs]);

  // Save rule: use backend id from API response
  const handleSaveRule = (ruleFromApi) => {
    setRules((prev) => [...prev, ruleFromApi]);
    // honor backend saveDisabled flag when a new rule is added
    try {
      if (ruleFromApi?.saveDisabled) {
        const key = getRowKey(ruleFromApi);
        if (key) setDisabledSaveIds((prev) => (prev.includes(key) ? prev : [...prev, key]));
      }
    } catch (e) {
      // ignore
    }
  };

  const handleDeleteRule = async (id) => {
    if (!canManageOverrides) return;
    const result = await dispatch(deleteParamConfig({ dynamicEndpoint: `${deleteParameterConfiguration}/${id}` }));
    if (result?.payload?.status === "SUCCESS") {
      const deletedRule = rules.find((r) => r.id === id);
      const keyToRemove = getRowKey(deletedRule);
      setRules((prev) => prev.filter((r) => r.id !== id));
      setDisabledSaveIds((prev) => prev.filter((k) => k !== keyToRemove));
      toast.success("Parameter configuration deleted successfully", { autoClose: 4000 });
    }
  };

  const handleEditRule = (rule) => {
    if (!canManageOverrides) return;
    setSelectedRule(rule);
    setModalMode("edit");
    setOpen(true);
  };

  const handleHighlightExisting = (id) => {
    if (!id) return;
    setHighlightedId(id);
    // scroll into view after render
    setTimeout(() => {
      const api = gridApiRef.current;
      if (api && typeof api.getRowNode === "function") {
        const node = api.getRowNode(String(id));
        if (node && typeof api.ensureNodeVisible === "function") {
          api.ensureNodeVisible(node, "middle");
        }
      }
      // clear highlight after 5s
      setTimeout(() => setHighlightedId(null), 5000);
    }, 50);
  };

  // Update rule: use backend id and all updated fields from API response
  const handleUpdateRule = (ruleFromApi) => {
    setRules((prev) =>
      prev.map((r) => (r.id === ruleFromApi.id ? { ...r, ...ruleFromApi } : r))
    );
    // Update save button disabled state according to backend `saveDisabled`
    const key = getRowKey(ruleFromApi);
    if (key) {
      if (ruleFromApi?.saveDisabled) {
        setDisabledSaveIds((prev) => (prev.includes(key) ? prev : [...prev, key]));
      } else {
        setDisabledSaveIds((prev) => prev.filter((k) => k !== key));
      }
    }
  };

  const handleCloseModal = () => {
    setOpen(false);
    setSelectedRule(null);
  };

  

  const columns = useMemo(() => {
    const baseColumns = [
      { headerName: "Plant Code", field: "plant", flex: 1 },
      { headerName: "TCCC Material Number", field: "material", flex: 1 },
      {
        headerName: "External Material Group",
        field: "sCode",
        flex: 1,
        cellRenderer: (params) => {
          const v = params?.data?.scode ?? params?.data?.sCode;
          if (Array.isArray(v)) return v.join(',');
          return v ?? "";
        },
      },
      { headerName: "MRP Controller", field: "mrpController", flex: 1 },
      { headerName: "Status (Active)", field: "isActive", flex: 1, cellRenderer: (params) => params.data.isStatusActive ? "Yes" : "No" },
    ];

    if (!canManageOverrides) return baseColumns;

    return [
      ...baseColumns,
      {
        headerName: "Actions",
        field: "actions",
        flex: 1,
        cellRenderer: (params) => (
          <Box display="flex"  gap={1} sx={{ width: "100%" }}>
            <IconButton size="small" color="primary" onClick={() => handleEditRule(params.data)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" sx={{ color: 'red' }} onClick={() => handleDeleteRule(params.data.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              sx={{ color: 'black' }}
              disabled={disabledSaveIds.includes(getRowKey(params.data))}
              onClick={async () => {
                const row = params.data;
                const payload = {
                  id: row.id ?? null,
                  plant: row.plant ? [String(row.plant)] : [],
                  material: row.material ? [String(row.material)] : [],
                  scode: row.scode ? [String(row.scode)] : (row.sCode ? [String(row.sCode)] : []),
                  mrpController: row.mrpController ? [String(row.mrpController)] : [],
                  high: {
                    pdtFrom: row.highPdtFrom ?? row.high?.pdtFrom ?? 0,
                    pdtTo: row.highPdtTo ?? row.high?.pdtTo ?? 0,
                    varFrom: row.highVarFrom ?? row.high?.varFrom ?? 0,
                    varTo: row.highVarTo ?? row.high?.varTo ?? 0,
                  },
                  medium: {
                    pdtFrom: row.mediumPdtFrom ?? row.medium?.pdtFrom ?? 0,
                    pdtTo: row.mediumPdtTo ?? row.medium?.pdtTo ?? 0,
                    varFrom: row.mediumVarFrom ?? row.medium?.varFrom ?? 0,
                    varTo: row.mediumVarTo ?? row.medium?.varTo ?? 0,
                  },
                  low: {
                    pdtFrom: row.lowPdtFrom ?? row.low?.pdtFrom ?? 0,
                    pdtTo: row.lowPdtTo ?? row.low?.pdtTo ?? 0,
                    varFrom: row.lowVarFrom ?? row.low?.varFrom ?? 0,
                    varTo: row.lowVarTo ?? row.low?.varTo ?? 0,
                  },
                };
                const result = await dispatch(saveParameterOverride({ data: payload }));
                if (result?.payload?.message || result?.payload?.status === 'SUCCESS') {
                  const msg = result?.payload?.message || 'Parameter risk configuration saved successfully';
                  toast.success(msg, { autoClose: 4000 });
                  // refresh active configs
                  dispatch(getActiveParameterConfigurations());
                  // disable the save button for this row
                  const key = getRowKey(params.data);
                  if (key) setDisabledSaveIds((prev) => (prev.includes(key) ? prev : [...prev, key]));
                }
              }}
            >
              <SaveIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ];
  }, [canManageOverrides, disabledSaveIds]);
  const overlayNoRowsTemplate = activeConfigsLoading
    ? '<span style="color:#6b7280;">Loading...</span>'
    : activeConfigsError
      ? '<span style="color:#ef4444;">Error loading data</span>'
      : '<span style="color:#6b7280;">No custom rules configured</span>';

  const gridOptions = useMemo(
    () => ({
      overlayNoRowsTemplate,
    }),
    [overlayNoRowsTemplate]
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="h6">Operational Overrides</Typography>
        <ButtonComponent
          label="Create Exception Rule"
          type="primary"
          bgColor="black"
          color="white"
          onClick={() => { setModalMode("create"); setOpen(true); }}
          disabled={!canManageOverrides}
        />
      </Box>
        <Box
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            width: "100%",
          }}
        >
          <DataTable
            columns={columns}
            rows={activeConfigsLoading || activeConfigsError ? [] : rules}
            height={400}
            gridOptions={gridOptions}
              defaultColDef={{
    resizable: true,
    filter: 'agSetColumnFilter',
    filterParams: {
      excelMode: 'windows',
      suppressMiniFilter: false,
      buttons: ['apply', 'reset'],
    },
  }}
            paginationProp={true}
            paginationPageSize={50}
            paginationPageSizeSelector={[50, 100, 150, 200]}
            getRowId={(params) => String(params.data?.id ?? "")}
            getRowStyle={(params) =>
              params?.data?.id === highlightedId ? { backgroundColor: "#fff4cc" } : null
            }
            onGridReady={(params) => {
              gridApiRef.current = params.api;
            }}
          />
        </Box>
<ParamSlidersModal
  open={open}
  onClose={handleCloseModal}
  onSave={handleSaveRule}
  mode={modalMode}
  initialRule={selectedRule}
  onUpdate={handleUpdateRule}
  rules={rules}
  onHighlightExisting={handleHighlightExisting}
  />
     
    
    </Box>
  );
};

export default OperationalOverrides;